import Link from "next/link";
import { Button as VortexButton } from "@vorseum/vortex/react/Button";
import { cn } from "@/lib/cn";

// VOR-145: este Button passou a ser um ADAPTER FINO sobre o Button do Vórtex
// DS (@vorseum/vortex/react/Button). O site agora consome o COMPONENTE do DS
// como primitivo de renderização (ganha a base canônica: rounded-full, alvo de
// toque mínimo 44px via `min-h-11`, sizing de ícone, anel de foco AAA), mas
// preserva a identidade visual da marca Vorseum no site:
//   • `primary` = CTA branco sobre fundo escuro (bg-white/text-slate-950)
//   • `ghost`   = superfície glass (borda + bg translúcidos)
// Essas classes de marca entram via `className` e, pelo `cn` do DS (twMerge),
// sobrescrevem os fills semânticos do DS (`bg-primary` etc.) sem regressão.
// A API local (variant/size/href polimórfico) é mantida para não tocar os
// call-sites.
const brandVariants = {
  primary:
    "bg-white font-semibold text-slate-950 hover:-translate-y-0.5 hover:bg-accent focus-visible:ring-accent focus-visible:ring-offset-canvas",
  ghost:
    "border border-white/10 bg-white/5 font-semibold text-white hover:-translate-y-0.5 hover:bg-white/10 focus-visible:ring-accent focus-visible:ring-offset-canvas",
} as const;

// VOR-94: tamanhos canônicos do site (CTA principal `md`, compacto `sm`).
const brandSizes = {
  md: "px-6 py-3.5 text-sm",
  sm: "px-4 py-2 text-xs",
} as const;

type ButtonVariant = keyof typeof brandVariants;
type ButtonSize = keyof typeof brandSizes;

type ButtonBaseProps = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
  children: React.ReactNode;
};

type ButtonAsLink = ButtonBaseProps & {
  href: string;
  target?: string;
  rel?: string;
  // VOR-145: o link aceita onClick de âncora (ex.: fechar menu mobile no
  // Header, tracking PostHog no comparativo) — disparado junto com a navegação.
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
  type?: never;
  disabled?: never;
};

type ButtonAsButton = ButtonBaseProps & {
  href?: never;
  target?: never;
  rel?: never;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  type?: "button" | "submit" | "reset";
  disabled?: boolean;
};

type ButtonProps = ButtonAsLink | ButtonAsButton;

export function Button({
  variant = "primary",
  size = "md",
  className,
  children,
  href,
  target,
  rel,
  onClick,
  type = "button",
  disabled,
}: ButtonProps) {
  const classes = cn(brandVariants[variant], brandSizes[size], className);

  // Link: usa `asChild` do DS para renderizar o <Link> do Next com os estilos
  // do Button do DS aplicados ao próprio <a>.
  if (href) {
    return (
      <VortexButton asChild className={classes}>
        <Link
          href={href}
          target={target}
          rel={rel}
          onClick={onClick as React.MouseEventHandler<HTMLAnchorElement> | undefined}
        >
          {children}
        </Link>
      </VortexButton>
    );
  }

  return (
    <VortexButton
      type={type}
      className={classes}
      onClick={onClick as React.MouseEventHandler<HTMLButtonElement> | undefined}
      disabled={disabled}
    >
      {children}
    </VortexButton>
  );
}
